import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import MarketplaceServices from '../services/MarketplaceServices';
import { FORM_CONSTANTS, VALIDATION_MESSAGES } from '../constants/formConstants';
import { handleApiError, logError } from '../utils/errorHandler';
import { processImageItem } from '../utils/urlUtils';
import { useBannerManager } from './useBannerManager';

export const usePublicationManager = (id, isEditing, reset, clearDraft) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  
  const { createExistingBanner } = useBannerManager();

  // Function to publish agent (legacy compatibility)
  const publishAgent = async (data, agentId) => {
    try {
      setSaving(true);
      setError(null);

      // Preparar dados usando a nova estrutura
      const publishData = MarketplaceServices.prepareAgentPublishData(data, agentId);
      
      console.log('📤 publishAgent - Enviando dados:', {
        agent_id: publishData.agent_id,
        agent_data_keys: Object.keys(publishData.agent_data),
        has_banner: !!publishData.banner,
        gallery_count: publishData.gallery_images.length
      });
      
      const response = await MarketplaceServices.publishAgent(publishData);
      
      if (response.status === 201 || response.data.success) {
        // Clear draft on successful publication
        if (clearDraft) {
          clearDraft();
        }

        // Show success message with marketplace URL if available
        const marketplaceUrl = response.data?.marketplace_url || response.data.data?.marketplace_url;
        let message = "Agente publicado no marketplace com sucesso!";
        
        if (marketplaceUrl) {
          message += `\n\nURL do Marketplace: ${marketplaceUrl}`;
        }

        await Swal.fire({
          title: "Sucesso!",
          text: message,
          icon: "success",
          confirmButtonText: "OK"
        });

        // Navigate to publications list
        navigate('/marketplace');
        
        return response.data;
      } else {
        throw new Error(response.data.message || `Erro ao publicar agente (HTTP ${response.status})`);
      }
    } catch (error) {
      logError("publishAgent", error, { agentId, data });
      const errorMessage = handleApiError(error, "Erro ao publicar agente no marketplace");
      setError(errorMessage);
      
      await Swal.fire({
        title: "Erro!",
        text: errorMessage,
        icon: "error",
        confirmButtonText: "OK"
      });
      
      throw error;
    } finally {
      setSaving(false);
    }
  };


  const loadPublication = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await MarketplaceServices.getPublication(id);
      
      // Verificar se a resposta segue a nova estrutura da API
      const publication = response.data?.status === 'success' 
        ? response.data.data 
        : response.data;
      
      console.log('📥 Dados da publicação recebidos:', publication);

      const formData = {
        // Mapeamento correto para a nova estrutura da API
        agent: publication.agent_id || publication.agent,
        agent_name: publication.agent_name || "",
        agent_description: publication.agent_description || "",
        agent_job: publication.agent_job || "",
        price: publication.price || publication.pricing_info?.price || "250.00",
        description: publication.description || publication.agent_description || "",
        video_url: publication.video_url || "",
        banner: createExistingBanner(publication.banner || publication.banner_url),
        gallery_images: publication.gallery_images ? publication.gallery_images.map((imageItem, index) => 
          processImageItem(imageItem, index)
        ) : [],
        categories: Array.isArray(publication.categories) ? publication.categories.map((cat) => cat.id || cat) : [publication.category].filter(Boolean),
        tags: publication.tags || [],
        capabilities: publication.capabilities || [],
        features: publication.features || [],
        detailed_content: publication.detailed_content || {
          overview: "",
          features_description: "",
          technical_specs: "",
        },
        technical_specs: (() => {
          try {
            return typeof publication.technical_specs === 'string' 
              ? JSON.parse(publication.technical_specs) 
              : publication.technical_specs || {};
          } catch (e) {
            return {};
          }
        })(),
        pricing_info: publication.pricing_info || {
          original_price: "",
          discount_percentage: publication.pricing_info?.discount_percentage || "",
          trial_period_days: "",
        },
        platform_name: publication.instructions?.platform_access?.platform_name || "",
        platform_url: publication.instructions?.platform_access?.platform_url || "",
        platform_description: publication.instructions?.platform_access?.platform_description || "",
        credential_type: publication.instructions?.access_credentials?.credential_type || "",
        access_instructions: publication.instructions?.access_credentials?.instructions || "",
        additional_info: publication.instructions?.access_credentials?.additional_info || "",
        configuration_steps: publication.instructions?.agent_configuration?.configuration_steps || "",
        customization_options: publication.instructions?.agent_configuration?.customization_options || "",
        setup_requirements: publication.instructions?.agent_configuration?.setup_requirements || "",
        video_tutorial_url: publication.instructions?.tutorial_video?.video_url || "",
        video_platform: publication.instructions?.tutorial_video?.video_platform || "",
        video_description: publication.instructions?.tutorial_video?.video_description || "",
        document_url: publication.instructions?.documentation?.document_url || "",
        document_type: publication.instructions?.documentation?.document_type || "",
        document_description: publication.instructions?.documentation?.document_description || "",
        contact_type: publication.instructions?.support_contact?.contact_type || "",
        contact_value: publication.instructions?.support_contact?.contact_value || "",
        contact_name: publication.instructions?.support_contact?.contact_name || "",
        support_hours: publication.instructions?.support_contact?.support_hours || "",
        playground_url: publication.playground_url || "",
      };
      
      console.log('📝 Dados formatados para o formulário:', {
        agent: formData.agent,
        agent_name: formData.agent_name,
        price: formData.price,
        categories: formData.categories,
        capabilities: formData.capabilities,
        banner: formData.banner,
        pricing_info: formData.pricing_info
      });
      
      console.log('🔄 Resetando formulário com dados da publicação...');
      reset(formData);
      
      return formData;
    } catch (error) {
      logError("loadPublication", error, { id, isEditing });
      const errorMessage = handleApiError(error, VALIDATION_MESSAGES.ERROR_LOADING);
      setError(errorMessage);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const submitPublication = async (data) => {
    try {
      setSaving(true);
      setError(null);

      // Extrair ID do agente (pode ser objeto ou ID direto)
      const agentId = data.agent?.id || data.agent;
      
      console.log('📎 Dados do agente:', {
        agent: data.agent,
        agent_type: typeof data.agent,
        agent_id: agentId,
        agent_name: data.agent_name,
        agent_job: data.agent_job
      });
      
      if (!agentId) {
        throw new Error("ID do agente é obrigatório");
      }

      // Validar dados antes do envio
      const validation = MarketplaceServices.validatePublicationData(data);
      if (!validation.isValid) {
        throw new Error(`Dados inválidos: ${validation.errors.join(', ')}`);
      }

      // Preparar dados conforme nova estrutura da API
      const publishData = MarketplaceServices.prepareAgentPublishData(data, agentId);
      
      console.log('📤 Enviando dados para API:', {
        isEditing,
        publication_id: id,
        agent_id: publishData.agent_id,
        agent_data_keys: Object.keys(publishData.agent_data),
        has_banner: !!publishData.banner,
        gallery_count: publishData.gallery_images.length
      });

      // Debug: verificar se métodos existem
      console.log('🔍 Métodos disponíveis:', {
        hasUpdatePublication: typeof MarketplaceServices.updatePublication === 'function',
        hasPublishAgent: typeof MarketplaceServices.publishAgent === 'function',
        allMethods: Object.getOwnPropertyNames(MarketplaceServices)
      });

      // Escolher método baseado no modo (criar vs editar)
      const response = isEditing 
        ? await MarketplaceServices.updatePublication(id, publishData)
        : await MarketplaceServices.publishAgent(publishData);

      // Verificar resposta (201 Created para criação, 200 OK para edição)
      const isSuccess = (response.status === 201 && !isEditing) || (response.status === 200 && isEditing) || response.data.success;
      if (isSuccess) {
        // Clear draft on successful publication
        if (clearDraft) {
          clearDraft();
        }

        // Show success message with marketplace URL if available
        const marketplaceUrl = response.data?.marketplace_url || response.data.data?.marketplace_url;
        let message = isEditing 
          ? "Publicação atualizada com sucesso!" 
          : "Agente publicado no marketplace com sucesso!";
        
        if (marketplaceUrl) {
          message += `\n\nURL do Marketplace: ${marketplaceUrl}`;
        }

        await Swal.fire({
          icon: "success",
          title: "Sucesso!",
          text: message,
          timer: FORM_CONSTANTS.SUCCESS_TIMER,
          showConfirmButton: false,
        });

        // Navigate to marketplace publications
        navigate("/marketplace");
      } else {
        throw new Error(response.data.message || `Erro na resposta da API (HTTP ${response.status})`);
      }
    } catch (error) {
      logError("submitPublication", error, { 
        agent_id: data.agent?.id || data.agent, 
        isEditing,
        form_data_keys: Object.keys(data)
      });

      const errorMessage = handleApiError(
        error,
        "Erro ao publicar agente no marketplace"
      );

      setError(errorMessage);

      await Swal.fire({
        title: "Erro!",
        text: errorMessage,
        icon: "error",
        confirmButtonText: "OK"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    if (!isEditing) {
      Swal.fire({
        title: VALIDATION_MESSAGES.CANCEL_CONFIRMATION,
        text: VALIDATION_MESSAGES.CANCEL_WARNING,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: VALIDATION_MESSAGES.CANCEL_YES,
        cancelButtonText: VALIDATION_MESSAGES.CANCEL_NO,
      }).then((result) => {
        if (result.isConfirmed) {
          navigate("/marketplace");
        }
      });
    } else {
      navigate("/marketplace");
    }
  };

  return {
    loading,
    saving,
    error,
    setError,
    loadPublication,
    submitPublication,
    publishAgent,
    handleCancel,
  };
};